// Typed wrappers over the Rust Tauri commands. Field names mirror the Rust structs.
import { t } from "@lingui/core/macro";
import { RemoteRpcError, assetUrl, invoke, isRemote, relayFetch } from "./rpc";
import { appLocalDataDir, documentDir, downloadDir, join } from "@tauri-apps/api/path";
import { accountFileName, pointerFileName, keychainAccountName, resolveDataRoot, getAccount, upsertAccount, activeAccountId, RIN_CHAT_DIR } from "./accounts";

export interface CardMeta {
  id: string;
  name: string;
  creator: string;
  spec_source: string;
  favorite: boolean;
  folder_id: string | null;
  updated_at: number;
  created_at: number;
  tags: string[];
  last_chat_at: number | null;
  chat_count: number;
  image: string | null;
  notes: string | null;
  /** `metadata.features` as indexed on the row (absent when the card has no flags) — what makes
   *  "is this card a persona?" answerable from the list without loading the card. */
  features?: Record<string, unknown>;
  /** The AICC card ID, when the site issued one (indexed on the row — no card load needed). */
  aicc_id?: string;
  /** The site version number the card started from ("v3"). */
  aicc_version_num?: number;
  /** The last update check's answer for this card: current | update | untracked | unavailable | unknown.
   *  Absent when never checked, or when a save has changed the card's ID or version since. */
  update_status?: string;
  /** The site's newer version number, when `update_status` offers one. */
  update_version_num?: number;
  /** card_save only: the automatic "edited" tag this save added (lib/editAutoTags.ts). */
  auto_tag?: string;
}

export interface Folder {
  id: string;
  parent_id: string | null;
  name: string;
  sort_order: number;
  created_at: number;
  hidden: boolean; // true = this folder's cards are excluded from the "All cards" view
  show_nested: boolean; // true = viewing this folder also lists cards from its nested folders
}

// The data root (holding the DB, blobs, and images) is user-configurable. Its location can't live in
// the DB (it says WHERE the DB is), so it's a plaintext pointer file in the OS app-data dir. Resolved
// once per session; changing it reloads the app so this cache is rebuilt.
// The resolved data root is cached for the session, but it is ACCOUNT-scoped: switching the active account
// (add-account, picker) MUST invalidate it. A stale root from the previous account handed to a destructive
// op (vault_reset) is exactly what deleted a populated vault's blobs/images during add-account/cancel.
let _dataRoot: string | null = null;
let _dataRootForId: string | null = null; // which account _dataRoot was computed for
function cacheDataRoot(v: string | null): void {
  _dataRoot = v;
  _dataRootForId = v ? activeAccountId() : null;
}
async function pointerPath(): Promise<string> {
  return join(await appLocalDataDir(), pointerFileName(activeAccountId()));
}
export async function dataDirGet(): Promise<string> {
  return invoke<string>("data_dir_get", { pointerPath: await pointerPath() });
}
export async function dataDirSet(dir: string): Promise<void> {
  await invoke<void>("data_dir_set", { pointerPath: await pointerPath(), dir });
  cacheDataRoot(dir.trim() || null); // keep the resolved-root cache in sync (empty = fall back to the default)
}
export async function pathExists(path: string): Promise<boolean> {
  return invoke<boolean>("path_exists", { path });
}
/** The active account's data root, but ONLY when it has gone missing (drive letter reassigned after an OS
 *  reinstall, folder moved or renamed) — else null. Every path under a vanished root fails with a bare
 *  "cannot find the path specified (os error 3)", so the crash panel probes the root itself rather than
 *  string-matching that message: an unrelated missing file must not offer to repoint the whole vault. */
export async function missingDataRoot(): Promise<string | null> {
  const root = await appRoot();
  return (await pathExists(root)) ? null : root;
}
/** Repoint the active account at a vault that MOVED — pointer + registry only, no file moves (unlike
 *  `changeDataDir`, which relocates data that is still where it was). Caller reloads. */
export async function relocateDataDir(root: string, base: string): Promise<void> {
  const entry = await getAccount(activeAccountId());
  await dataDirSet(root);
  if (entry) await upsertAccount({ id: entry.id, username: entry.username, basePath: base }, Date.now());
  cacheDataRoot(root);
}
/** Fallback root for the active account when its pointer file isn't set yet. Computed from the registry
 *  (base + username). Onboarding/migration set the pointer explicitly before any data access, so this is a
 *  last resort; if the account isn't even registered yet, use a legacy-shaped default. */
export async function defaultDataRoot(): Promise<string> {
  const entry = await getAccount(activeAccountId());
  if (entry) return resolveDataRoot(entry);
  return join(await documentDir(), RIN_CHAT_DIR);
}

/** One-time on boot (idempotent): carry a pre-rebrand vault (identifier com.aicharactercards.chat /
 *  Documents/aicc-chat) over to the new identity so the rebrand doesn't orphan an existing install. */
export async function migrateLegacyIdentity(): Promise<void> {
  try {
    await invoke<void>("migrate_legacy_identity", {
      newAppLocalDir: await appLocalDataDir(),
      documentsDir: await documentDir(),
    });
  } catch { /* best-effort — a fresh install has nothing to migrate */ }
}

/** The resolved data root — the override pointer if set, else Documents/aicc-chat. */
export async function appRoot(): Promise<string> {
  if (_dataRoot && _dataRootForId === activeAccountId()) return _dataRoot;
  const override = (await dataDirGet()).trim();
  cacheDataRoot(override || (await defaultDataRoot()));
  return _dataRoot!;
}
export async function blobsDir(): Promise<string> {
  return join(await appRoot(), "blobs");
}
export async function legacyBlobsDir(): Promise<string> {
  return join(await appLocalDataDir(), "blobs");
}
/** The encrypted DB, alongside blobs + images under the data root. */
export async function dbPath(): Promise<string> {
  return join(await appRoot(), "aicc.db");
}
/** Old location (%LOCALAPPDATA%), relocated to dbPath() on first run of this build. */
export async function legacyDbPath(): Promise<string> {
  return join(await appLocalDataDir(), "aicc.db");
}

/** Move the whole data root (DB + blobs + images) to `newRoot`, reopening the DB. Caller reloads. */
export async function changeDataDir(newRoot: string): Promise<void> {
  const oldRoot = await appRoot();
  if (oldRoot === newRoot) return;
  await claimDataRoot(newRoot); // refuse to move into a folder owned by another account (before any file moves)
  await invoke<void>("data_dir_migrate", {
    oldDb: await join(oldRoot, "aicc.db"),
    newDb: await join(newRoot, "aicc.db"),
    oldBlobs: await join(oldRoot, "blobs"),
    newBlobs: await join(newRoot, "blobs"),
    oldImages: await join(oldRoot, "images"),
    newImages: await join(newRoot, "images"),
  });
  await dataDirSet(newRoot);
  cacheDataRoot(newRoot);
}

// vault
/** Claim this account's data root (writes/checks a `.rinchat-vault` owner marker). Throws if the folder
 *  already belongs to a DIFFERENT local account — two vaults can't share one root (one encrypted DB, one
 *  key). Safe/idempotent for the owner; call before opening or committing to a root. */
export async function claimDataRoot(root?: string): Promise<void> {
  return invoke<void>("claim_data_root", { root: root ?? (await appRoot()), accountId: activeAccountId() });
}
/** Undo a claim (abandoned Add-account): removes ONLY this account's ownership marker, never data. */
export async function releaseDataRoot(root?: string): Promise<void> {
  return invoke<void>("release_data_root", { root: root ?? (await appRoot()), accountId: activeAccountId() });
}
/** Move ONE account's data (DB, blobs, images, exports, marker) from `oldRoot` to `newRoot` — scoped, so a
 *  shared parent `rin-chat/` dir isn't disturbed. Idempotent + non-destructive. */
export async function migrateDataRoot(oldRoot: string, newRoot: string): Promise<void> {
  return invoke<void>("migrate_data_root", { oldRoot, newRoot });
}
export async function dbInit(): Promise<void> {
  await claimDataRoot(); // guard: never open a DB in a folder owned by another account
  return invoke<void>("db_init", { dbPath: await dbPath(), legacyPath: await legacyDbPath() });
}
/** Machine-local account file (password-wrapped DEK) — lives next to the data-dir pointer, readable before
 *  the (possibly relocated) data root is opened. */
async function accountPath(): Promise<string> {
  return join(await appLocalDataDir(), accountFileName(activeAccountId()));
}
/** The OS keychain entry name for the active account's silent unlock (default = "dek-v1"). */
const keychainAccount = (): string => keychainAccountName(activeAccountId());
export interface VaultStatus {
  has_keychain: boolean; // silent unlock available on this device
  has_account: boolean; // a username+password local account exists
  username: string | null;
  has_aicc_recovery: boolean; // an AICC-password reset wrap is stored on this device
  biometric: boolean; // silent unlock is gated behind Windows Hello / Touch ID
}
/** What this OS calls its identity prompt. UI copy must use ONE of these — never "Windows Hello / Touch ID",
 *  which reads as a choice the user has to make. Platform is fixed per build, so derive it from the webview
 *  UA rather than paying a round trip. */
export function biometricName(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Macintosh") || ua.includes("Mac OS X")) return "Touch ID";
  if (ua.includes("Windows")) return "Windows Hello";
  return "biometric unlock";
}
/** `"available"`, or a human-readable reason why not (nothing enrolled, blocked by policy, sensor busy…).
 *  Show the reason — a silent "unavailable" leaves users with no idea what to fix. */
export const biometricAvailability = (): Promise<string> => invoke<string>("biometric_available");
/** Show the OS identity prompt. Resolves true when the user passed, false when cancelled/failed. */
export const biometricAuthenticate = (): Promise<boolean> => invoke<boolean>("biometric_authenticate");
/** Turn the Hello/Touch ID gate on the silent unlock on or off. */
export const accountSetBiometric = async (enabled: boolean): Promise<void> =>
  invoke<void>("account_set_biometric", { accountPath: await accountPath(), keychainAccount: keychainAccount(), enabled });
/** Toggle plain "remember me" (silent unlock) immediately from the open vault — no password needed. */
export const accountSetRemember = async (enabled: boolean): Promise<void> =>
  invoke<void>("account_set_remember", { accountPath: await accountPath(), keychainAccount: keychainAccount(), enabled });
export const vaultStatus = async (): Promise<VaultStatus> =>
  invoke<VaultStatus>("vault_status", { accountPath: await accountPath(), keychainAccount: keychainAccount() });
export const vaultUnlock = () => invoke<boolean>("vault_unlock", { keychainAccount: keychainAccount() }); // keychain silent unlock
/** Is the vault already open? Rust-side state, so a second window can skip the unlock decision. */
export const vaultIsUnlocked = () => invoke<boolean>("vault_is_unlocked");
/** Lock the session. `forget` also drops the silent-unlock key — EXCEPT when biometric unlock is on, since
 *  that key is exactly what Hello / Touch ID releases (the Rust side enforces this). */
export const vaultLock = async (forget: boolean) =>
  invoke<void>("vault_lock", { accountPath: await accountPath(), keychainAccount: keychainAccount(), forget });
/** Reset this device's vault: clears the keychain + account file + pointer and removes the app's own data
 *  files (DB, blobs, images, exports) so onboarding starts clean. Destructive — restore from a backup.
 *
 *  `dataRoot` selects WHICH data files to delete: omit it to use the active account's resolved root (the
 *  full "reset my vault" case). Pass "" to delete NO data and only drop the id-scoped identity files —
 *  the safe path for abandoning a half-created NEW account, where `appRoot()` would otherwise fall back to
 *  the SHARED `Documents/rin-chat` and could wipe a legacy account's flat data. */
export async function vaultReset(dataRoot?: string): Promise<void> {
  await invoke<void>("vault_reset", {
    accountPath: await accountPath(),
    keychainAccount: keychainAccount(),
    pointerPath: await pointerPath(),
    dataRoot: dataRoot !== undefined ? dataRoot : await appRoot(),
    accountId: activeAccountId(), // Rust only deletes data whose .rinchat-vault marker names THIS account
  });
  cacheDataRoot(null); // the pointer is gone — re-resolve on next launch
}
/** Store an AICC-password reset wrap of the unlocked DEK (call after linking the AICC account). */
export const accountSetAiccRecovery = async (aiccPassword: string): Promise<void> =>
  invoke<void>("account_set_aicc_recovery", { accountPath: await accountPath(), aiccPassword });
/** Remove the AICC-password reset wrap from the account file (call on unlink). */
export const accountClearAiccRecovery = async (): Promise<void> =>
  invoke<void>("account_clear_aicc_recovery", { accountPath: await accountPath() });
/** Unlock via the AICC-password reset wrap (forgot-local-password path). */
export const vaultUnlockAicc = async (aiccPassword: string): Promise<void> =>
  invoke<void>("vault_unlock_aicc", { accountPath: await accountPath(), aiccPassword });
export type RecoveryInfo = { mnemonic: string; recovery_file_text: string };
/** First run: create the vault under a username + password (+ keychain if `remember`). */
export const vaultInit = async (username: string, password: string, remember: boolean): Promise<RecoveryInfo> =>
  invoke<RecoveryInfo>("vault_init", { accountPath: await accountPath(), keychainAccount: keychainAccount(), username, password, remember });
/** Unlock a returning vault with the account password. */
export const vaultUnlockPassword = async (password: string, remember: boolean): Promise<void> =>
  invoke<void>("vault_unlock_password", { accountPath: await accountPath(), keychainAccount: keychainAccount(), password, remember });
/** Check the password WITHOUT unlocking anything or touching the keychain — the screen lock's gate. */
export const vaultVerifyPassword = async (password: string): Promise<boolean> =>
  invoke<boolean>("vault_verify_password", { accountPath: await accountPath(), password });
/** Wrap the currently-unlocked DEK under a (new) username+password — used after a recovery. */
export const accountSetPassword = async (username: string, password: string, remember: boolean): Promise<void> =>
  invoke<void>("account_set_password", { accountPath: await accountPath(), keychainAccount: keychainAccount(), username, password, remember });
/** Restore the vault key from the recovery phrase + recovery file (device migration / keychain loss). */
/** Restore from EITHER input — the phrase alone, or the file's contents alone (the file just carries the
 *  same words). Pass "" for whichever the user didn't give. Legacy 12-word kits still need both. */
export const vaultRecover = (mnemonic: string, recoveryFileContents: string) =>
  invoke<void>("vault_recover", { keychainAccount: keychainAccount(), mnemonic, recoveryFileContents });
/** Re-issue a recovery kit (fresh phrase + file) for the unlocked vault — for a user who didn't save theirs. */
export const recoveryRegenerate = (): Promise<RecoveryInfo> => invoke<RecoveryInfo>("recovery_regenerate");

// AICC website account linking (native-side HTTP → not subject to the Tauri origin's CORS).
export const AICC_API_BASE = "https://api.aicharactercards.com/api";
export const AICC_SITE = "https://aicharactercards.com"; // the frontend (for the /link-desktop page)
/** Serialize the local AICC recovery wrap into a portable blob to upload to the account. */
export const aiccRecoveryExport = async (): Promise<string> =>
  invoke<string>("aicc_recovery_export", { accountPath: await accountPath() });
/** Import a server-fetched recovery blob (new device): unwrap the DEK with the AICC password → in memory. */
export const aiccRecoveryImport = (blobJson: string, aiccPassword: string): Promise<void> =>
  invoke<void>("aicc_recovery_import", { blobJson, aiccPassword });
/** Upload / fetch the recovery blob on the linked account (server stores ciphertext only). */
export const aiccPutRecoveryBlob = (token: string, blob: string): Promise<string> =>
  invoke<string>("aicc_put_recovery_blob", { baseUrl: AICC_API_BASE, token, blob, vaultId: activeAccountId() });
export const aiccGetRecoveryBlob = (token: string, vaultId?: string): Promise<string> =>
  invoke<string>("aicc_get_recovery_blob", { baseUrl: AICC_API_BASE, token, vaultId: vaultId ?? activeAccountId() });
/** Fetch (issuing on first call) this account's server-held recovery secret — the stand-in for an AICC
 *  password when the link was made through the browser, where no password is ever seen. */
export const aiccGetRecoverySecret = (token: string, vaultId?: string): Promise<string> =>
  invoke<string>("aicc_get_recovery_secret", { baseUrl: AICC_API_BASE, token, vaultId: vaultId ?? activeAccountId() });
/** Delete THIS vault's recovery copy on the AICC account (the blob and the server-held secret). Resolves the
 *  HTTP status; Rust refuses an empty vault id, since the route deletes every vault without one. */
export const aiccDeleteRecoveryBlob = (token: string): Promise<number> =>
  invoke<number>("aicc_delete_recovery_blob", { baseUrl: AICC_API_BASE, token, vaultId: activeAccountId() });
/** Vaults this account has recovery data for — for the "which vault to restore" choice on a new device. */
export type RecoveryVault = { vaultId: string; updatedAt: string };
export const aiccListRecoveryVaults = async (token: string): Promise<RecoveryVault[]> => {
  const raw = await invoke<string>("aicc_list_recovery_vaults", { baseUrl: AICC_API_BASE, token });
  try { return (JSON.parse(raw).vaults ?? []) as RecoveryVault[]; } catch { return []; }
};
/** Fetch subscription entitlements (raw JSON). */
export const aiccGetEntitlements = (token: string): Promise<string> =>
  invoke<string>("aicc_get_entitlements", { baseUrl: AICC_API_BASE, token });
/** Link via the system browser (loopback) — returns the access token + refresh token; the desktop never
 *  sees the password. `refresh` is "" if the site build predates refresh tokens. */
export async function aiccLinkViaBrowser(): Promise<{ token: string; refresh: string }> {
  const raw = await invoke<string>("aicc_link_via_browser", { siteUrl: AICC_SITE });
  const parsed = JSON.parse(raw) as { token: string; refresh?: string };
  return { token: parsed.token, refresh: parsed.refresh ?? "" };
}
/** `alphaTester` is the users-table grant for the Alpha Tester API; /me and login both select it. */
/** An AICC contest prize grant (the Halloween 2026 contest): LLM access through the Alpha Tester proxy, text
 *  only, a daily token budget. `usedToday` here is stale (/me refreshes rarely); GET /api/llm/usage is live. */
export interface ContestApiGrant { active: boolean; startsAt: string; endsAt: string; dailyTokens: number; usedToday: number }
/** `emailVerified`: the aicharactercards.com account's own email status, from /auth/me and login. NOT the rin.chat
 *  account's `emailVerified` (rinAccount.ts), which is a different account. DISPLAY ONLY: the site enforces what
 *  it gates on its server, a modified client could fake this, and the cached copy is stale until the next /me. */
export interface AiccUser { id: number; username: string; email: string; role?: string; alphaTester?: boolean; emailVerified?: boolean; contestApi?: ContestApiGrant | null; [k: string]: unknown }
/** Sign in to aicharactercards.com (as a desktop client → 60-min access token + refresh token). Throws the
 *  server message on failure. `refreshToken` is undefined if the server build predates refresh tokens. */
export async function aiccLogin(emailOrUsername: string, password: string): Promise<{ user: AiccUser; token: string; refreshToken?: string }> {
  const raw = await invoke<string>("aicc_login", { baseUrl: AICC_API_BASE, emailOrUsername, password });
  const parsed = JSON.parse(raw) as { user: AiccUser; token: string; refreshToken?: string };
  return { user: parsed.user, token: parsed.token, refreshToken: parsed.refreshToken };
}
/** Exchange a refresh token for a fresh access token (server rotates the refresh token). Throws
 *  "session expired (HTTP …)" when the refresh token is dead/reused/expired → the caller re-signs. */
export async function aiccRefresh(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
  const raw = await invoke<string>("aicc_refresh", { baseUrl: AICC_API_BASE, refreshToken });
  const parsed = JSON.parse(raw) as { token: string; refreshToken: string };
  return { token: parsed.token, refreshToken: parsed.refreshToken };
}
/** Best-effort revoke of a refresh token (sign-out / unlink). */
export const aiccLogout = (refreshToken: string): Promise<void> =>
  invoke<void>("aicc_logout", { baseUrl: AICC_API_BASE, refreshToken });
/** Validate a stored token + fetch the current profile (null if the session is invalid/expired). */
export async function aiccFetchMe(token: string): Promise<AiccUser | null> {
  try {
    return await aiccFetchMeStrict(token);
  } catch {
    return null;
  }
}
/** Like `aiccFetchMe` but THROWS the underlying reason — "session invalid (HTTP 401)" vs a native network/
 *  DNS/TLS error string. Used by the browser-link flow so a failed validation can report why (a second
 *  machine can't reach api.aicharactercards.com, or its clock is wrong) instead of a generic message. */
export async function aiccFetchMeStrict(token: string): Promise<AiccUser> {
  const raw = await invoke<string>("aicc_fetch_me", { baseUrl: AICC_API_BASE, token });
  return JSON.parse(raw) as AiccUser;
}

// images — always under the data root (the single configurable location).
export async function defaultImagesDir(): Promise<string> {
  return join(await appRoot(), "images");
}
export async function imagesDir(): Promise<string> {
  return defaultImagesDir();
}
export async function encryptImages(): Promise<boolean> {
  return (await settingsGet("images_encrypted")) !== "false"; // default: encrypted
}
/**
 * URL for the `aicc-img://` custom protocol — the webview loads (decrypted) images natively,
 * no base64/IPC. `key` = card id, or `persona_<id>`. `convertFileSrc` emits the correct form
 * per platform (Windows/Android → http://aicc-img.localhost/…, macOS/iOS/Linux → aicc-img://…).
 */
export function imgSrc(key: string, v?: string | number): string {
  const base = assetUrl(key, "aicc-img");
  const q = bust(key, v);
  return q ? `${base}?v=${q}` : base; // v busts the cache when the image changes
}

/**
 * How many times each image key was written in this app session, part of every image URL. A screen's own
 * counter (bumped by CARD_IMAGE_CHANGED_EVENT) only covers a screen open at the time: one opened AFTER a
 * change started back at 0, asked for the same URL as before it, and got the cached old picture. The card
 * editor and a chat opened after regenerating art both did.
 */
const imageWrites = new Map<string, number>();
const bust = (key: string, v?: string | number): string => {
  const n = imageWrites.get(key);
  return n ? `${n}.${v ?? 0}` : v === undefined ? "" : String(v);
};

/**
 * Downscaled tile image (max 384px), built + cached on first request. Card art is routinely
 * 12MB+/4000px — decoding that for a ~126px grid tile costs ~100MB of bitmap each, so the
 * library grid must use this, not imgSrc.
 */
export function thumbSrc(key: string, v?: string | number, anim?: boolean): string {
  const base = assetUrl(key, "aicc-img");
  const q = anim ? "thumb=1&anim=1" : "thumb=1"; // anim=1 → animated WebP tile for APNG art (static fallback otherwise)
  const b = bust(key, v);
  return b ? `${base}?v=${b}&${q}` : `${base}?${q}`;
}

/** True for a card whose art animates. The stored `image` mime carries the flag: `image/apng` for animated
 *  PNG, `image/awebp` for animated WebP (what a card downloaded from the site brings — it re-encodes every
 *  image, so animated art always arrives as WebP). */
export const isAnimatedArt = (image: string | null | undefined): boolean =>
  image === "image/apng" || image === "image/awebp";

/** Pre-build any missing thumbnails (runs off the UI thread). Returns how many were built. */
export async function thumbsBackfill(): Promise<number> {
  return invoke<number>("thumbs_backfill", {});
}

/** One-shot backfill: flag cards whose art is an APNG saved before animated detection existed. Returns how
 *  many were newly flagged (0 on repeat runs). Cheap for already-flagged libraries. */
export async function cardsDetectAnimated(): Promise<number> {
  return invoke<number>("cards_detect_animated", {});
}

/** Distinct installed system font family names, sorted — for the chat-font picker. One-shot (parses the
 *  system's font files), so call it when the picker opens, not at startup. Empty on any failure. */
export async function listSystemFonts(): Promise<string[]> {
  try { return await invoke<string[]>("list_system_fonts", {}); } catch { return []; }
}
/** This computer's RAM and graphics cards (src-tauri/src/hardware.rs), for the model finder. Desktop only. */
export const hardwareInfo = () => invoke<import("./modelFit").Hardware>("hardware_info", {});
/** How many thumbnails a backfill would build right now (cheap; used to warn before a big one). */
export async function thumbsPendingCount(): Promise<number> {
  return invoke<number>("thumbs_pending_count", {});
}
/** Tell the Rust protocol handler where images live (call at boot + when the folder changes). */
export async function imageDirSet(): Promise<void> {
  return invoke<void>("image_dir_set", { dir: await imagesDir() });
}
/**
 * The generic keyed image store — a file per key, no database row required.
 *
 * `cardImageSet` is NOT this: it updates the `cards` row first and fails with "card no longer exists"
 * for any key that isn't a card. Anything keyed by something else (persona avatars, chat images) belongs
 * here. Returns the stored image's MIME, which `imageGet` needs back to rebuild the data URI.
 */
/** Delete a card's own stored image (`asset_<uuid>` only). Used to clean up files the card editor wrote for
 *  images that were added and then removed before the card was ever saved — `card_save`'s own sweep can only
 *  see what a SAVED card once referenced. */
export const imageDelete = (key: string): Promise<void> => invoke<void>("image_delete", { key });

export async function imageSet(key: string, dataUri: string): Promise<string> {
  const mime = await invoke<string>("image_set", {
    key,
    dataUri,
    encrypt: await encryptImages(),
  });
  notifyCardImage(key);
  return mime;
}

export async function imageGet(key: string, mime: string): Promise<string | null> {
  return invoke<string | null>("image_get", { key, mime });
}

/** Extract plain text from a document the frontend can't read itself (pdf/docx/odt/epub) — Rust side,
 *  pure compute on the bytes passed. Throws with a readable message on unsupported/corrupt files. */
/** Every key in the settings table — names only, no values. Powers the backup's denylist coverage. */
export const settingsListKeys = () => invoke<string[]>("settings_list_keys");

export async function fileExtractText(name: string, bytesB64: string): Promise<string> {
  return invoke<string>("file_extract_text", { name, bytesB64 });
}

/** Fetch a webpage and reduce it to readable text (Card Forge "attach a page"). */
export async function webFetchText(url: string): Promise<string> {
  return invoke<string>("web_fetch_text", { url });
}

export async function cardImageGet(id: string): Promise<string | null> {
  return invoke<string | null>("card_image_get", { id });
}
/**
 * A card's (or persona's) image was replaced or removed. `detail` is the image key.
 *
 * The image URL is the SAME for a given key, and responses are cacheable, so nothing about a rewrite
 * makes an already-rendered `<img>` fetch again — an open chat kept showing the old avatar until the
 * app restarted. Screens that display images listen for this and bump a counter into the `?v=`
 * cache-buster. Fired from the two writers below so every caller is covered: the card editor, the
 * Forge, the image generator, and personas (which use this same API with a `persona_<id>` key).
 */
export const CARD_IMAGE_CHANGED_EVENT = "aicc:card-image-changed";
const notifyCardImage = (key: string) => {
  imageWrites.set(key, (imageWrites.get(key) ?? 0) + 1);
  try {
    window.dispatchEvent(new CustomEvent(CARD_IMAGE_CHANGED_EVENT, { detail: key }));
  } catch {
    /* no window (tests) */
  }
};

/** `userChange`: the person replaced the art (upload, generate, crop) — the only kind that can earn the automatic
 *  "new art" tag. Imports, merges, restores and duplicates leave it off. Returns the tag it added, if any. */
export async function cardImageSet(id: string, dataUri: string, userChange = false): Promise<string | null> {
  const tag = await invoke<string | null>("card_image_set", {
    id,
    dataUri,
    encrypt: await encryptImages(),
    userChange,
  });
  notifyCardImage(id);
  if (tag) announceAutoTag(id, tag);
  return tag ?? null;
}

/** Settings → Automatic tags added `tag` to card `id` behind whatever has the card open. Editors holding the card
 *  merge it into their copy (without saving), or their next save would erase it. */
export const CARD_AUTO_TAG_EVENT = "aicc:card-auto-tag";
export function announceAutoTag(id: string, tag: string): void {
  // Next task, not now: the saving caller updates its own saved-state refs right after its await, and the listener
  // must see those, or it would mark a clean editor dirty.
  setTimeout(() => {
    try {
      window.dispatchEvent(new CustomEvent<{ id: string; tag: string }>(CARD_AUTO_TAG_EVENT, { detail: { id, tag } }));
    } catch {
      /* no window (tests) */
    }
  }, 0);
}
/** Subscribe to automatic tags added to card `id`; returns the unsubscribe. */
export function onCardAutoTag(id: string, fn: (tag: string) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent<{ id: string; tag: string }>).detail;
    if (d?.id === id) fn(d.tag);
  };
  window.addEventListener(CARD_AUTO_TAG_EVENT, h);
  return () => window.removeEventListener(CARD_AUTO_TAG_EVENT, h);
}

/** Settings' prompt compare saved card `id`'s two prompts behind whatever has the card open (CardEditor, the chat
 *  drawer, the Forge). Same contract as an automatic tag: holders merge it into their copy without saving, or
 *  their next save would put the old text back. */
export type CardPromptsPatch = { system_prompt: string; post_history_instructions: string };
export const CARD_PROMPTS_EVENT = "aicc:card-prompts";
export function announceCardPrompts(id: string, prompts: CardPromptsPatch): void {
  setTimeout(() => { // next task, for the same reason as announceAutoTag
    try { window.dispatchEvent(new CustomEvent(CARD_PROMPTS_EVENT, { detail: { id, prompts } })); } catch { /* no window (tests) */ }
  }, 0);
}
export function onCardPrompts(id: string, fn: (prompts: CardPromptsPatch) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent<{ id: string; prompts: CardPromptsPatch }>).detail;
    if (d?.id === id) fn(d.prompts);
  };
  window.addEventListener(CARD_PROMPTS_EVENT, h);
  return () => window.removeEventListener(CARD_PROMPTS_EVENT, h);
}
/** Remove a card's image (delete blob + null the DB column) so it falls back to the placeholder. */
export async function cardImageClear(id: string): Promise<void> {
  await invoke<void>("card_image_clear", { id });
  notifyCardImage(id);
}

// bulk import (folder)
/** List every card file (*.png / *.json) under a folder, recursively. */
export const scanCardFiles = (dir: string) => invoke<string[]>("scan_card_files", { dir });
/** Read a file's bytes as base64. */
export const readFileB64 = (path: string) => invoke<string>("read_file_b64", { path });
/** Fetch an http(s) URL natively (no webview CORS) and return the body as base64. For URL/card-ID imports.
 *
 *  Remotely the same call goes through the provider relay instead of the (denied) command, which is what
 *  makes a local ComfyUI reachable from another device: every ComfyUI URL is built from the provider's
 *  own base, so the desktop's "is this a provider I configured?" check passes. An arbitrary URL — a card
 *  import by address — does NOT pass, and that refusal is the SSRF defence working, not a bug. */
export async function httpGetBytes(url: string): Promise<string> {
  if (!isRemote()) return invoke<string>("http_get_bytes", { url });
  const res = await relayFetch(url, { method: "GET" });
  if (!res.ok) { const status = res.status; throw new Error(t`Fetch failed (${status})`); }
  return bytesToBase64(new Uint8Array(await res.arrayBuffer()));
}
/** POST JSON from RUST, so a local server (SwarmUI, ComfyUI, an A1111 fork) doesn't need CORS headers.
 *  Long default timeout — an image generation on a busy GPU legitimately takes minutes. */
export async function httpPostJson(url: string, body: unknown, timeoutSecs?: number): Promise<string> {
  if (!isRemote()) return invoke<string>("http_post_json", { url, body: JSON.stringify(body), timeoutSecs });
  // Same relay, same provider-base check as httpGetBytes — this is the half that queues a ComfyUI job.
  const res = await relayFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) { const status = res.status; throw new Error(t`Request failed (${status})`); }
  return await res.text();
}

// The conversions themselves live in `base64.ts`; re-exported here because this is where the callers that
// need them are already importing from.
export { base64ToBytes, bytesToBase64 } from "./base64";
import { base64ToBytes, bytesToBase64 } from "./base64";

// cards
let cardSaveChain: Promise<unknown> = Promise.resolve(); // see chatSaveChain — same out-of-order guard
export async function cardSave(cardJson: string, folderId: string | null): Promise<CardMeta> {
  const enc = await encryptImages();
  const p = cardSaveChain.then(() => trackWrite(invoke<CardMeta>("card_save", { cardJson, encryptImages: enc, folderId })));
  cardSaveChain = p.catch(() => {});
  const meta = await p;
  if (meta.auto_tag) announceAutoTag(meta.id, meta.auto_tag);
  return meta;
}
/** Merge an imported card INTO an existing card (`targetId`), overwriting its content + art but keeping its
 *  folder, tags, notes, favorite. Used by the import dedup flow when a card matches an existing one by
 *  name+creator (e.g. re-importing to restore lost art without duplicating or re-filing). */
export async function cardMergeImport(cardJson: string, targetId: string): Promise<void> {
  return invoke<void>("card_merge_import", {
    cardJson,
    encryptImages: await encryptImages(),
    targetId,
  });
}
/** Pass as folderId to list only cards with no folder (maps to folder_id IS NULL server-side). */
export const UNFILED = "__unfiled__";
// --- AICC site: the card routes (lib/aiccSite.ts) and the site-sync bookkeeping on the card rows (lib/siteSync.ts).
/** One request to an AICC card route. Resolves to `{"status", "body"}` JSON for every server answer — errors
 *  included, since those routes put the reason in the body; rejects only on a network failure. */
export const aiccSiteRequest = (method: string, path: string, token?: string, body?: string): Promise<string> =>
  invoke<string>("aicc_site_request", { baseUrl: AICC_API_BASE, method, path, token: token ?? null, body: body ?? null });
/** Send a card FILE to an AICC card route (multipart) — staging it for the site's own submit/edit form, which
 *  is all the app's upload does. Same `{"status", "body"}` answer as `aiccSiteRequest`; `fields` is normally
 *  empty, since the person fills in the listing on that form. */
export const aiccCardUpload = (path: string, token: string, fileName: string, pngB64: string, fields?: Record<string, string>): Promise<string> =>
  invoke<string>("aicc_card_upload", { baseUrl: AICC_API_BASE, path, token, fileName, pngB64, fields: fields ?? null });
export interface CardSiteRow {
  id: string;
  aicc_id: string | null;
  /** The update-check key: the site version ROW id the card started from. */
  aicc_version_id: number | null;
  /** The background indexer has read this card's link from its blob. */
  indexed: boolean;
  /** The one-time match scan has looked at this card. */
  site_scanned: boolean;
}
export const cardsSiteRows = () => invoke<CardSiteRow[]>("cards_site_rows");

/** One file's outcome in the Library's import log (storage.rs ImportLogEntry). */
/** `bundle` = a line about a bundle file itself (what it added, a lorebook choice) rather than a card. */
export type ImportOutcome = "imported" | "merged" | "imported_new" | "skipped_identical" | "skipped_match" | "failed" | "bundle";
export interface ImportLogEntry {
  run_id: string;
  /** ms */
  at: number;
  source?: string | null;
  name?: string | null;
  creator?: string | null;
  outcome: ImportOutcome;
  /** The card created or updated. */
  card_id?: string | null;
  /** The existing card it matched or duplicated. */
  match_id?: string | null;
  /** Error text or reason. */
  detail?: string | null;
}
export interface ImportRun {
  run_id: string;
  /** When the run's first entry was recorded (ms). */
  at: number;
  source: string | null;
  total: number;
  counts: Partial<Record<ImportOutcome, number>>;
}
export const importLogAdd = (entries: ImportLogEntry[]) => invoke<void>("import_log_add", { entries });
/** Runs newest first; pass the last run's `at` as `beforeAt` for the next page. */
/** `before` is the last run already shown, when paging: its start time and, for a tie, its id. */
export const importLogRuns = (limit: number, before?: { at: number; runId: string }) =>
  invoke<ImportRun[]>("import_log_runs", { limit, beforeAt: before?.at ?? null, beforeRun: before?.runId ?? null });
export const importLogEntries = (runId: string) => invoke<ImportLogEntry[]>("import_log_entries", { runId });
export const importLogClear = () => invoke<void>("import_log_clear");
export interface CardSiteUpdate {
  id: string;
  scanned?: boolean;
  update_status?: string;
  update_version_num?: number | null;
}
export const cardsSiteSet = (updates: CardSiteUpdate[]) => invoke<void>("cards_site_set", { updates });
/** Re-open the match scan for every card that still has no AICC ID; returns how many. */
export const cardsSiteRescan = () => invoke<number>("cards_site_rescan");
/** Link a card to its site card without a full save (updated_at, org and chats untouched). */
export const cardSetAiccLink = (cardId: string, aiccId: string, versionId: number | null, versionNum: number | null) =>
  invoke<void>("card_set_aicc_link", { cardId, aiccId, versionId, versionNum });

export const cardList = (folderId: string | null, query: string | null) =>
  invoke<CardMeta[]>("card_list", { folderId, query });
export const cardSetFavorite = (id: string, favorite: boolean) =>
  invoke<void>("card_set_favorite", { id, favorite });
export const cardSetFolder = (id: string, folderId: string | null) =>
  invoke<void>("card_set_folder", { id, folderId });
/** Restore a card's original timestamps (used by backup restore so dates/sort order survive). */
export const cardSetTimestamps = (id: string, createdAt: number, updatedAt: number) =>
  invoke<void>("card_set_timestamps", { id, createdAt, updatedAt });
/** Restore a chat's original timestamps (used by backup restore so recency ordering survives).
 *  `createdAt: null` keeps the stored one. */
export const chatSetTimestamps = (id: string, createdAt: number | null, updatedAt: number) =>
  invoke<void>("chat_set_timestamps", { id, createdAt, updatedAt });
/** Windows-only toast whose CLICK actually works (foregrounds + routes to the chat) — the notification
 *  plugin's onAction never fires on Windows desktop. Rejects on other platforms; caller falls back. */
export const notifyClickthrough = (title: string, body: string, cardId: string, chatId: string) =>
  invoke<void>("notify_clickthrough", { title, body, cardId, chatId });
/** OS spelling suggestions for one word (Windows Spell Checking API). Empty when spelled fine,
 *  unavailable, or not on Windows. */
export const spellSuggest = (word: string) => invoke<string[]>("spell_suggest", { word });
/** Add a word to the user's Windows custom dictionary (no-op elsewhere). Desktop only — a phone
 *  spell-checks in its own browser, so this is DENY for remote clients. */
export const spellAdd = (word: string) => invoke<void>("spell_add", { word });
export const cardsMoveToFolder = (ids: string[], folderId: string | null) =>
  invoke<number>("cards_move_to_folder", { ids, folderId });
export const cardsMoveByTag = (tag: string, folderId: string | null) =>
  invoke<number>("cards_move_by_tag", { tag, folderId });
export const allTags = () => invoke<string[]>("all_tags");
/** Merge source tag(s) into `target` across every card (also a rename when sources = [old]). Returns #changed. */
export const tagsMerge = (sources: string[], target: string) => invoke<number>("tags_merge", { sources, target });
/** Apply an explicit old→new tag mapping across every card (case-normalize, bulk find/replace). Returns #changed. */
export const tagsRewrite = (mapping: Record<string, string>) => invoke<number>("tags_rewrite", { mapping });
/** Remove tag(s) from every card. Returns #changed. */
export const tagsDelete = (names: string[]) => invoke<number>("tags_delete", { names });
/** Add tag(s) to specific cards (by id). Returns #changed. */
export const cardsAddTags = (ids: string[], add: string[]) => invoke<number>("cards_add_tags", { ids, add });
/** Remove tag(s) from specific cards (by id). Returns #changed. */
export const cardsRemoveTags = (ids: string[], remove: string[]) => invoke<number>("cards_remove_tags", { ids, remove });

export interface FolderCount {
  folder_id: string | null;
  count: number;
  /** How many of `count` are personas, which Not in Folder leaves out. */
  personas: number;
}
/** Per-folder card counts (+ a null bucket for unfiled) — the sidebar can't derive these
 *  from the folder-filtered card list. */
export const folderCounts = () => invoke<FolderCount[]>("folder_counts");
export const favoriteCount = () => invoke<number>("favorite_count");
/** Cards flagged persona/adventure_persona — the sidebar hides its Personas row when this is 0. */
export const personaCount = () => invoke<number>("persona_count");
export const cardLoad = (id: string) => invoke<string>("card_load", { id });
export async function cardDelete(id: string, deleteChats: boolean): Promise<void> {
  return invoke<void>("card_delete", { id, deleteChats });
}

// folders
export const folderCreate = (name: string, parentId: string | null) =>
  invoke<Folder>("folder_create", { name, parentId });
export const folderRename = (id: string, name: string) =>
  invoke<void>("folder_rename", { id, name });
export const folderDelete = (id: string) => invoke<void>("folder_delete", { id });
/** Re-parent a folder (newParentId = null → top level), carrying its subtree. */
export const folderMove = (id: string, newParentId: string | null) =>
  invoke<void>("folder_move", { id, newParentId });
/** Hide/show a folder's cards in the "All cards" view. */
export const folderSetHidden = (id: string, hidden: boolean) =>
  invoke<void>("folder_set_hidden", { id, hidden });
export const folderSetShowNested = (id: string, showNested: boolean) =>
  invoke<void>("folder_set_show_nested", { id, showNested });
export const folderList = () => invoke<Folder[]>("folder_list");
/** Max nesting depth (top-level folders are depth 1). */
export const MAX_FOLDER_DEPTH = 4;

// chats
export interface ChatMeta {
  id: string;
  card_id: string;
  title: string;
  msg_count: number;
  updated_at: number;
}
// Save-order guard: the Rust write commands are (async) — they run on a thread pool, so two saves of
// the same entity issued back-to-back could complete OUT OF ORDER and the older blob would win the
// atomic rename. Serializing each write family through a promise chain restores issue order without
// blocking the UI thread. Reads stay unchained.
let chatSaveChain: Promise<unknown> = Promise.resolve();
/** Settles once every chat save queued so far has landed (lib/chatClose.ts waits on it before a delete). */
export const chatSavesSettled = (): Promise<unknown> => chatSaveChain;
let wbSaveChain: Promise<unknown> = Promise.resolve();

export async function chatSave(chatJson: string): Promise<ChatMeta> {
  const p = chatSaveChain.then(() => trackWrite(invoke<ChatMeta>("chat_save", { chatJson })));
  chatSaveChain = p.catch(() => {});
  return p;
}
/** chatSave for a backup restore: a chat whose card is not in the library is kept, not refused. */
export async function chatRestore(chatJson: string): Promise<ChatMeta> {
  const p = chatSaveChain.then(() => trackWrite(invoke<ChatMeta>("chat_restore", { chatJson })));
  chatSaveChain = p.catch(() => {});
  return p;
}
export const chatList = (cardId: string) => invoke<ChatMeta[]>("chat_list", { cardId });
/** Every chat row, those whose card was deleted with "keep chats" included. Desktop only (backup). */
export const chatListAll = () => invoke<ChatMeta[]>("chat_list_all");
/** Most recently used chats across ALL cards, newest first — the Library's "All chats" view. */
export const chatListRecent = (limit: number) => invoke<ChatMeta[]>("chat_list_recent", { limit });

/** One generated picture variant as one chat holds it. The bytes are image key `chatimg_<variantId>`. */
export interface ChatImageRow {
  variantId: string;
  mime: string;
  prompt: string;
  width: number;
  height: number;
  chatId: string;
  cardId: string;
  chatTitle: string;
  /** The message's time, unix ms. */
  msgTs: number;
}
/** Every generated chat picture across every chat, newest first (a branch's copy is its own row). Slow on
 *  a big library: it decrypts every chat. */
export const chatImagesList = () => invoke<ChatImageRow[]>("chat_images_list");

export interface ChatHit {
  id: string;
  card_id: string;
  card_name: string;
  title: string;
  msg_count: number;
  updated_at: number;
}
/** Global chat search by title (joined to the owning card's name). */
export const chatSearch = (query: string, limit = 10) =>
  invoke<ChatHit[]>("chat_search", { query, limit });
export const chatLoad = (id: string) => invoke<string>("chat_load", { id });
export const chatDelete = (id: string) => invoke<void>("chat_delete", { id });

// worldbooks (standalone World Info, stored as encrypted blobs like chats)
export interface WorldbookMeta {
  id: string;
  name: string;
  entry_count: number;
  created_at: number;
  updated_at: number;
}
export async function worldbookSave(worldbookJson: string): Promise<WorldbookMeta> {
  const p = wbSaveChain.then(() => trackWrite(invoke<WorldbookMeta>("worldbook_save", { worldbookJson })));
  wbSaveChain = p.catch(() => {});
  return p;
}
export const worldbookList = () => invoke<WorldbookMeta[]>("worldbook_list");
export const worldbookLoad = (id: string) => invoke<string>("worldbook_load", { id });
export const worldbookDelete = (id: string) => invoke<void>("worldbook_delete", { id });

// forge sessions (one encrypted blob each, like chats — see forgeSessions.ts for why they moved)
export interface ForgeSessionMetaRow {
  id: string;
  /** The DERIVED display name (sessionName), computed before saving: the picker has no transcript to
   *  derive it from, which is the whole point of not loading one. */
  name: string;
  saved_id: string | null;
  msg_count: number;
  has_card: boolean;
  created_at: number;
  updated_at: number;
  /** "card" or "lorebook". Indexed so the picker's badge and the "already open?" lookup skip the blob. */
  mode: string;
  /** The standalone subject's library id, when the subject is not a card. Never `saved_id`. */
  subject_id: string | null;
}
let forgeSaveChain: Promise<unknown> = Promise.resolve();
export async function forgeSessionSave(sessionJson: string, name: string): Promise<ForgeSessionMetaRow> {
  // Chained like chats and worldbooks: two saves of the SAME session racing would otherwise let the older
  // one write its blob last.
  const p = forgeSaveChain.then(() => trackWrite(invoke<ForgeSessionMetaRow>("forge_session_save", { sessionJson, name })));
  forgeSaveChain = p.catch(() => {});
  return p;
}
export const forgeSessionList = () => invoke<ForgeSessionMetaRow[]>("forge_session_list");
export const forgeSessionLoad = (id: string) => invoke<string>("forge_session_load", { id });
export const forgeSessionDelete = (id: string) => invoke<void>("forge_session_delete", { id });

// Story Maker stories (one sealed blob each, like forge sessions). Times are ms.
export interface StoryMetaRow { id: string; name: string; page_count: number; source_kind: string; created_at: number; updated_at: number }
let storySaveChain: Promise<unknown> = Promise.resolve();
/** null: a story with no plan and no pages is a draft, which is neither written nor listed. */
export function storySave(storyJson: string): Promise<StoryMetaRow | null> {
  // Chained for the same reason as forge sessions: an older save of the same story must not land last.
  const p = storySaveChain.then(() => trackWrite(invoke<StoryMetaRow | null>("story_save", { storyJson })));
  storySaveChain = p.catch(() => {});
  return p;
}
export const storyList = () => invoke<StoryMetaRow[]>("story_list");
export const storyLoad = (id: string) => invoke<string>("story_load", { id });
export const storyDelete = (id: string) => invoke<void>("story_delete", { id });

/** Dev-only MCP bridge gate: closes the bridge socket whenever a personal (non-'claude') account is
 *  selected or unlocked, reopens it for the test account. Fired eagerly on account SELECTION so
 *  connections die before a password is typed. Harmless no-op in release builds. */
export const mcpGate = (blocked: boolean) => invoke<void>("mcp_gate", { blocked }).catch(() => {});

// settings (encrypted key/value), with a read-through cache. Every settings load in the app funnels
// here, and each used to be its own IPC round-trip — opening the Settings modal fired dozens of
// settings_get calls, and hot paths (prompt configs per impersonation draft, adventure settings per
// chat open) re-fetched keys that hadn't changed. Sound only while every write passes through THIS
// module: settingsSet updates it, cross-window writes clear it via appEvents, and a REMOTE client's
// relayed settings_set clears its key in serveRemoteRpc (remoteAccess.ts) — that one bypassed the
// cache entirely and left the desktop stale until restart. Flows that swap the database underneath
// (backup restore, account switch) reload the page, which clears it.
const settingsCache = new Map<string, Promise<string | null>>();

// --- Pending-write tracking -----------------------------------------------------------------------------
// Sign-out clears the DEK; a save still in flight at that moment fails silently — the classic way to
// lose the message typed seconds before signing out. Every write in this module registers here, and
// signOut awaits `whenWritesIdle()` before locking. Bounded wait: a hung write must not trap the user
// on a screen they asked to leave.
let pendingWrites = 0;
const writeWaiters: Array<() => void> = [];
function trackWrite<T>(p: Promise<T>): Promise<T> {
  pendingWrites++;
  const done = () => {
    pendingWrites--;
    if (pendingWrites === 0) for (const w of writeWaiters.splice(0)) w();
  };
  p.then(done, done);
  return p;
}
export function whenWritesIdle(timeoutMs = 4000): Promise<void> {
  if (pendingWrites === 0) return Promise.resolve();
  return new Promise((res) => {
    const t = setTimeout(res, timeoutMs);
    writeWaiters.push(() => { clearTimeout(t); res(); });
  });
}

export const settingsSet = (key: string, value: string) => {
  settingsCache.set(key, Promise.resolve(value)); // optimistic — readers see the new value immediately
  const p = trackWrite(invoke<void>("settings_set", { key, value }));
  p.catch(() => settingsCache.delete(key)); // write failed — fall back to re-reading the truth
  return p;
};
/**
 * Drop cached settings so the next read goes back to the database.
 *
 * The cache above is only sound while this webview is the sole writer, and it stopped being that the day
 * Settings could pop out: a pop-out is a second webview with its own heap and its own copy of this map, so
 * anything it saves leaves every other window holding a stale value FOREVER. Announcing the change was not
 * enough — the listener re-read and got its own cache back, which is why colors changed in a popped-out
 * Settings never reached the app behind it. Cross-window events call this before their handlers run
 * (see appEvents.ts); it is deliberately whole-map, because an event names a topic, not a settings key.
 */
export function forgetSettingsCache(key?: string): void {
  if (key === undefined) settingsCache.clear();
  else settingsCache.delete(key);
}

export const settingsGet = (key: string): Promise<string | null> => {
  let p = settingsCache.get(key);
  if (!p) {
    p = invoke<string | null>("settings_get", { key }).catch((e: unknown) => {
      // A REMOTE client is refused the keys that hold credentials (`providers`, `aicc_account`, …) and the
      // desktop-only ones. That is the design, not a failure, and it is permanent for those keys — so it
      // reads as "not set" rather than throwing. Without this, one denied key at boot takes down the whole
      // remote session, and no allowlist tuning can ever fix it because the denial is deliberate.
      if (e instanceof RemoteRpcError && e.status === 403) return null;
      throw e;
    });
    settingsCache.set(key, p);
    p.catch(() => settingsCache.delete(key)); // a failed read must not cache as "no value"
  }
  return p;
};

// Per-card version-history snapshots (row-per-snapshot table). `value` is an opaque JS-encoded payload
// (gzip+base64 or raw JSON); the Rust side just seals/returns it. See lib/cardHistory.ts.
export const cardSnapshotPush = (cardId: string, ts: number, value: string, cap: number) =>
  invoke<void>("snapshot_push", { cardId, ts, value, cap });
export const cardSnapshotList = (cardId: string) =>
  invoke<Array<{ ts: number; value: string }>>("snapshot_list", { cardId });
export const cardSnapshotClear = (cardId: string) => invoke<void>("snapshot_clear", { cardId });

// Chat vectorization chunk store (row per chunk). Like snapshots, `value` is an opaque JS-encoded
// payload — the Rust side only seals and returns it, so the vector layout is not a schema migration.
// See lib/vectorStore.ts.
export const vecPut = (chatId: string, rows: Array<{ key: string; value: string }>) =>
  invoke<void>("vec_put", { chatId, rows });
export const vecList = (chatId: string) =>
  invoke<Array<{ key: string; value: string }>>("vec_list", { chatId });
export const vecDelete = (chatId: string, keys: string[]) => invoke<void>("vec_delete", { chatId, keys });
export const vecClear = (chatId: string) => invoke<void>("vec_clear", { chatId });
export const vecCount = (chatId: string) => invoke<number>("vec_count", { chatId });

/** Load a JSON-backed setting, merged over `def` (missing/corrupt → `def`). */
export async function loadJsonSetting<T>(key: string, def: T): Promise<T> {
  const raw = await settingsGet(key);
  if (!raw) return def;
  try {
    const parsed = JSON.parse(raw) as unknown;
    // An ARRAY setting must come back as an array. Spreading one into the object merge below turned
    // `[entry]` into `{ "0": entry }` — no `.length`, so a caller checking for an empty list saw one every
    // time. The pending-uploads list never resolved a single upload because of it.
    if (Array.isArray(def)) return (Array.isArray(parsed) ? parsed : def) as T;
    // Same trap for a PRIMITIVE setting, and worse: `{ ...false, ...true }` is `{}`, and `{ ...false,
    // ...false }` is also `{}` — both truthy, so a saved `false` read back as true. A string came back as
    // a map of its characters. The merge below only makes sense when the default is an object.
    if (def === null || typeof def !== "object") return (typeof parsed === typeof def ? parsed : def) as T;
    return { ...def, ...(parsed as Partial<T>) };
  } catch {
    return def;
  }
}
export const saveJsonSetting = <T>(key: string, value: T) => settingsSet(key, JSON.stringify(value));

// Exports go to the OS Downloads folder by default (where users expect saved/downloaded files).
/**
 * Hand a file to a REMOTE client instead of writing it to the desktop's Downloads folder.
 *
 * This is what "REDIRECT" meant for the export commands: the capability is wanted remotely, but writing
 * to the desktop's disk is the wrong mechanism — the person is on another machine and expects the file
 * there. So the browser downloads it. Returns the filename, which is what callers report back ("Saved
 * to …"); there is no server path to name.
 */
function downloadInBrowser(filename: string, blob: Blob): string {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on a delay: revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return filename;
}


export async function saveExport(filename: string, content: string): Promise<string> {
  if (isRemote()) return downloadInBrowser(filename, new Blob([content], { type: "application/octet-stream" }));
  const dir = await downloadDir();
  return invoke<string>("save_export", { dir, filename, content });
}
/** Write a binary export (base64) to the Downloads folder — e.g. a PNG card. Returns the path. */
export async function saveExportBytes(filename: string, dataB64: string): Promise<string> {
  if (isRemote()) return downloadInBrowser(filename, new Blob([base64ToBytes(dataB64)], { type: "application/octet-stream" }));
  const dir = await downloadDir();
  return invoke<string>("save_export_bytes", { dir, filename, dataB64 });
}
/** Write a LARGE binary export to Downloads by streaming it in chunks, base64-encoding each chunk on its own.
 *  A single base64 of a big archive (e.g. a full backup) overflows the JS max string length — this never
 *  builds a string larger than one chunk. Returns the final path. */
export async function saveExportBytesChunked(filename: string, bytes: Uint8Array, onProgress?: (pct: number) => void): Promise<string> {
  // The chunking exists to avoid overflowing JS string length while base64-ing for IPC. A browser
  // download takes the bytes directly, so none of that applies.
  if (isRemote()) { onProgress?.(100); return downloadInBrowser(filename, new Blob([bytes as unknown as BlobPart], { type: "application/octet-stream" })); }
  const dir = await downloadDir();
  const CHUNK = 6 * 1024 * 1024; // 6 MiB (divisible by 3 → clean base64) → ~8 MB string per call
  const chunks = Math.max(1, Math.ceil(bytes.length / CHUNK));
  let path = "";
  for (let i = 0; i < chunks; i++) {
    const slice = bytes.subarray(i * CHUNK, (i + 1) * CHUNK);
    path = await invoke<string>("save_export_chunk", { dir, filename, dataB64: bytesToBase64(slice), first: i === 0, last: i === chunks - 1 });
    onProgress?.(Math.round(((i + 1) / chunks) * 100));
  }
  return path;
}
/** Write text to a user-chosen absolute path (from a Save dialog) — used for the recovery file. */
export async function saveTextTo(path: string, content: string): Promise<string> {
  return invoke<string>("save_text_to", { path, content });
}
/** Move existing card art when the images folder changes, so it isn't orphaned. Returns count moved. */
export const imagesMove = (oldDir: string, newDir: string) =>
  invoke<number>("images_move", { oldDir, newDir });

/** One-time: move blobs from the old app-data location into Documents/aicc-chat/blobs. */
export async function blobsRelocate(): Promise<void> {
  return invoke<void>("blobs_relocate", { oldDir: await legacyBlobsDir(), newDir: await blobsDir() });
}

/** A read of the OS Windows clipboard history (see src-tauri/src/clipboard_history.rs). `status`:
 *  "ok" | "disabled" | "denied" | "unsupported" | "error"; `message` explains anything but "ok". */
export interface ClipHistory { status: string; items: string[]; message: string | null }
export const clipboardHistoryList = () => invoke<ClipHistory>("clipboard_history_list");

/**
 * Warm the settings cache with one call instead of one per key.
 *
 * In-process a settings read is free, so this changes nothing on the desktop. Over a network each read is
 * a round trip — boot issues dozens — and on a VPN that is what start-up feels like. Keys the caller may
 * not read (credentials, desktop-only) simply come back absent, exactly as an unset key does.
 */
export async function settingsPrefetch(keys: string[]): Promise<void> {
  const missing = keys.filter((k) => !settingsCache.has(k));
  if (!missing.length) return;
  try {
    const got = await invoke<Record<string, string>>("settings_get_many", { keys: missing });
    for (const k of missing) settingsCache.set(k, Promise.resolve(got[k] ?? null));
  } catch {
    // Falls back to individual reads — a failed prefetch must never be worse than not prefetching.
  }
}

/** A card carrying an embedded lorebook — the Worldbooks panel's "Character lorebooks" section. */
export interface CardLorebookMeta {
  id: string;
  name: string;
  book_name: string;
  entry_count: number;
}
export const cardsWithLorebooks = () => invoke<CardLorebookMeta[]>("cards_with_lorebooks");
